import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Layers, Youtube, Film, MessageSquare,
  CreditCard, Settings, FolderOpen, ChevronDown, ChevronRight,
  ChevronLeft, LogOut, ImageIcon
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LogoC } from '@/components/brand/LogoMarks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Main navigation items
const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/creators', label: 'Creators', icon: Users, agencyOnly: true },
  { path: '/brands', label: 'Brands', icon: Layers },
];

// Content creation sections
const CONTENT_SECTIONS = [
  { 
    path: '/youtube', 
    label: 'YouTube', 
    icon: Youtube,
    description: 'Extract clips from long videos'
  },
  { 
    path: '/reels-creator', 
    label: 'Reels Creator', 
    icon: Film,
    description: 'Build reels from short clips'
  },
  { 
    path: '/talking-videos', 
    label: 'Talking Videos', 
    icon: MessageSquare,
    description: 'Edit talking-head content'
  },
];

// Bottom navigation
const BOTTOM_ITEMS = [
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userType, creators, activeCreatorId, setActiveCreatorId,
    activeBrandId, setActiveBrandId, activeCreator, activeBrand, trialActive, trialDaysLeft,
    sidebarCollapsed, setSidebarCollapsed, currentUser, logout } = useApp();
  
  const [brollExpanded, setBrollExpanded] = useState(false);

  const navItems = NAV_ITEMS.filter(item => {
    if (item.agencyOnly && userType !== 'agency') return false;
    return true;
  });

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <TooltipProvider>
      <aside
        className="sidebar flex-shrink-0 flex flex-col"
        style={{ width: sidebarCollapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 py-4 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <LogoC size={26} />
              <span className="font-semibold text-sm tracking-tight" style={{ color: 'hsl(var(--foreground))' }}>
                Reelia
              </span>
              {trialActive && (
                <Badge className="text-xs px-1.5 py-0.5 badge-warning border rounded" style={{ fontSize: '10px', padding: '1px 6px' }}>
                  Trial
                </Badge>
              )}
            </div>
          )}
          {sidebarCollapsed && (
            <div className="mx-auto">
              <LogoC size={26} />
            </div>
          )}
          {!sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-1 rounded hover:bg-surface-hover"
              style={{ color: 'hsl(var(--foreground-subtle))' }}
            >
              <ChevronLeft size={14} />
            </button>
          )}
        </div>

        {/* Collapse toggle when collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="flex items-center justify-center w-full py-2"
            style={{ color: 'hsl(var(--foreground-subtle))' }}
          >
            <ChevronRight size={14} />
          </button>
        )}

        {/* Brand selector */}
        {!sidebarCollapsed && (
          <div className="px-3 py-3 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
            {userType === 'agency' && (
              <div className="mb-2">
                <Select value={activeCreatorId} onValueChange={setActiveCreatorId}>
                  <SelectTrigger className="h-8 text-xs" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
                    <SelectValue placeholder="Select creator" />
                  </SelectTrigger>
                  <SelectContent>
                    {creators.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'hsl(var(--primary) / 0.2)', color: 'hsl(var(--primary))' }}>
                            {c.avatar}
                          </div>
                          {c.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {activeCreator && (
              <Select value={activeBrandId} onValueChange={setActiveBrandId}>
                <SelectTrigger className="h-8 text-xs" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {activeCreator.brands.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }} />
                        {b.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {activeBrand && (
              <p className="text-xs mt-2 px-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                {activeBrand.content_type || 'General'}
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {/* Main nav items */}
          {sidebarCollapsed ? (
            navItems.map(item => (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`sidebar-nav-item w-full justify-center mb-1 ${isActive(item.path) ? 'active' : ''}`}
                    style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                  >
                    <item.icon size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ))
          ) : (
            navItems.map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`sidebar-nav-item w-full mb-1 ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon size={16} />
                <span>{item.label}</span>
              </button>
            ))
          )}

          {/* Divider */}
          {!sidebarCollapsed && (
            <div className="my-3 mx-2">
              <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                Content Tools
              </p>
            </div>
          )}
          {sidebarCollapsed && <div className="my-2 border-t" style={{ borderColor: 'hsl(var(--border))' }} />}

          {/* Content creation sections */}
          {sidebarCollapsed ? (
            CONTENT_SECTIONS.map(item => (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`sidebar-nav-item w-full justify-center mb-1 ${isActive(item.path) ? 'active' : ''}`}
                    style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                  >
                    <item.icon size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="font-medium">{item.label}</p>
                  <p className="text-xs opacity-70">{item.description}</p>
                </TooltipContent>
              </Tooltip>
            ))
          ) : (
            CONTENT_SECTIONS.map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`sidebar-nav-item w-full mb-1 ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon size={16} />
                <span>{item.label}</span>
              </button>
            ))
          )}

          {/* B-Roll Library */}
          {!sidebarCollapsed && (
            <div className="mt-2">
              <button
                onClick={() => setBrollExpanded(!brollExpanded)}
                className="sidebar-nav-item w-full mb-1 justify-between"
              >
                <div className="flex items-center gap-2">
                  <ImageIcon size={16} />
                  <span>B-Roll Library</span>
                </div>
                {brollExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {brollExpanded && (
                <div className="ml-4 pl-2 border-l" style={{ borderColor: 'hsl(var(--border))' }}>
                  <button
                    onClick={() => navigate('/broll/vertical')}
                    className={`sidebar-nav-item w-full mb-1 text-xs ${isActive('/broll/vertical') ? 'active' : ''}`}
                  >
                    <FolderOpen size={14} />
                    <span>Vertical</span>
                  </button>
                  <button
                    onClick={() => navigate('/broll/horizontal')}
                    className={`sidebar-nav-item w-full mb-1 text-xs ${isActive('/broll/horizontal') ? 'active' : ''}`}
                  >
                    <FolderOpen size={14} />
                    <span>Horizontal</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {sidebarCollapsed && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => navigate('/broll')}
                  className={`sidebar-nav-item w-full justify-center mb-1 ${isActive('/broll') ? 'active' : ''}`}
                  style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                >
                  <ImageIcon size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">B-Roll Library</TooltipContent>
            </Tooltip>
          )}

          {/* Divider */}
          {!sidebarCollapsed && <div className="my-3 border-t" style={{ borderColor: 'hsl(var(--border))' }} />}
          {sidebarCollapsed && <div className="my-2 border-t" style={{ borderColor: 'hsl(var(--border))' }} />}

          {/* Bottom nav items */}
          {sidebarCollapsed ? (
            BOTTOM_ITEMS.map(item => (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`sidebar-nav-item w-full justify-center mb-1 ${isActive(item.path) ? 'active' : ''}`}
                    style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                  >
                    <item.icon size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ))
          ) : (
            BOTTOM_ITEMS.map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`sidebar-nav-item w-full mb-1 ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon size={16} />
                <span>{item.label}</span>
              </button>
            ))
          )}
        </nav>

        {/* Platform disclaimer */}
        {!sidebarCollapsed && (
          <div className="mx-3 mb-2 px-2 py-2">
            <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))', opacity: 0.6, lineHeight: 1.4 }}>
              Each creator is responsible for understanding the rules and policies of the platforms they publish on.
            </p>
          </div>
        )}

        {/* Trial banner */}
        {!sidebarCollapsed && trialActive && (
          <div className="mx-3 mb-3 p-3 rounded-lg" style={{ background: 'hsl(var(--primary) / 0.08)', border: '1px solid hsl(var(--primary) / 0.2)' }}>
            <p className="text-xs font-semibold mb-1" style={{ color: 'hsl(var(--primary))' }}>
              {trialDaysLeft} days left in trial
            </p>
            <p className="text-xs mb-2" style={{ color: 'hsl(var(--foreground-muted))' }}>Exports have Reelia watermark</p>
            <Button
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => navigate('/billing')}
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}
            >
              Upgrade
            </Button>
          </div>
        )}

        {/* User footer */}
        {!sidebarCollapsed && (
          <div className="px-3 py-3 border-t flex items-center gap-2" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{ background: 'hsl(var(--primary) / 0.2)', color: 'hsl(var(--primary))' }}
            >
              {currentUser?.avatar || (userType === 'agency' ? 'AG' : 'CR')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
                {currentUser?.name || 'My Account'}
              </p>
              <p className="text-xs truncate" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                {currentUser?.email || ''}
              </p>
            </div>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              title="Sign out"
              data-testid="logout-btn"
              className="p-1 rounded hover:bg-surface-hover"
              style={{ color: 'hsl(var(--foreground-subtle))', flexShrink: 0 }}
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
};
